/**
 * Case-by-case v3.7 → v3.8 upgrade offers (elios-backend wallet-service 0.75.0, `upgrade-offer/`).
 *
 * Product rules: a v3.7 wallet may stay on v3.7 forever; a migration to v3.8 starts only once an
 * operator offered it to that wallet from this console, then the owner accepts and signs it in the
 * app. No self-serve, no forcing. A withdrawn offer (or the `walletMigrationStart` kill switch)
 * stops new starts and early retries only, never a migration already moving.
 *
 * Pure selectors for Wallet 360's Offer / Withdraw buttons and the offers list. The backend is the
 * authority: these only decide what to offer, mirroring its documented button rules.
 */
import type { WalletDoc, WalletUpgradeOfferDoc, WithDocId } from "@/firestore/types";
import { describeGeneration, FIXED_GENERATION, REGISTERED_GENERATIONS } from "./generations";
import type { CommandAvailability } from "./migrations";
import { migrationStartRefusalLabel } from "./migrationCohorts";

export const UPGRADE_OFFER_STATES = ["offered", "accepted", "consumed", "withdrawn"] as const;
export type UpgradeOfferState = (typeof UPGRADE_OFFER_STATES)[number];

/** States in which the offer stands: the owner may start (`offered`) or has (`accepted`). */
export const LIVE_OFFER_STATES: readonly UpgradeOfferState[] = ["offered", "accepted"];

export function isLiveOffer(
  offer: Pick<WalletUpgradeOfferDoc, "state"> | null | undefined,
): boolean {
  return !!offer && (LIVE_OFFER_STATES as readonly string[]).includes(offer.state);
}

/** Deployment order of a registered generation, or null (unknown / unregistered). */
function ordinal(generation: unknown): number | null {
  const key = describeGeneration(generation).key;
  const index = REGISTERED_GENERATIONS.findIndex((g) => g.key === key);
  return index < 0 ? null : index;
}

/** Whether `generation` is a registered generation older than `target`. */
export function isOlderGeneration(generation: unknown, target: string = FIXED_GENERATION): boolean {
  const from = ordinal(generation);
  const to = ordinal(target);
  return from !== null && to !== null && from < to;
}

/**
 * The Offer button (`POST /admin/wallets/{walletId}/upgradeOffer`): the wallet is `provisioned`,
 * the owner's current wallet, on a generation older than the target, with no live offer. `offer`
 * is the `walletUpgradeOffers/{walletId}` read: `undefined` while loading, `null` when absent;
 * `offerReadable: false` when the collection cannot be read here (not deployed yet).
 */
export function offerAvailability(input: {
  wallet: Pick<WithDocId<WalletDoc>, "_docId" | "state" | "contractGeneration" | "currentWallet">;
  generation: string | null;
  offer: Pick<WalletUpgradeOfferDoc, "state"> | null | undefined;
  offerReadable: boolean;
  target?: string;
}): CommandAvailability {
  const target = input.target ?? FIXED_GENERATION;
  const { wallet, offer } = input;
  if (!input.offerReadable)
    return {
      offered: false,
      note: "Upgrade offers cannot be read in this environment yet (wallet-service 0.75.0, its rules and indexes).",
    };
  if (offer === undefined) return { offered: false, note: "Loading the wallet's offer…" };
  if (wallet.state !== "provisioned")
    return { offered: false, note: `The wallet is ${wallet.state}, not provisioned.` };
  if (wallet.currentWallet && wallet.currentWallet !== wallet._docId)
    return {
      offered: false,
      note: `Not the owner's current wallet: offer the upgrade on ${wallet.currentWallet} instead.`,
    };
  if (!input.generation)
    return {
      offered: false,
      note: "The wallet's contract generation is not projected yet (backfill pending), so the console cannot tell it is older than the target.",
    };
  if (!isOlderGeneration(input.generation, target))
    return {
      offered: false,
      note: `The wallet is on ${input.generation}: nothing older than ${target} to upgrade.`,
    };
  if (isLiveOffer(offer))
    return {
      offered: false,
      note: `An offer is already ${offer!.state}. Withdraw it before offering again.`,
    };
  return {
    offered: true,
    note: `Offers ${input.generation} → ${target} to this wallet. The owner's app shows "Upgrade available"; the owner accepts and signs the migration on their device.`,
  };
}

/** The Withdraw button (`DELETE /admin/wallets/{walletId}/upgradeOffer`): a live offer. */
export function withdrawAvailability(
  offer: Pick<WalletUpgradeOfferDoc, "state"> | null | undefined,
): CommandAvailability {
  if (!offer || !isLiveOffer(offer))
    return { offered: false, note: offer ? `The offer is ${offer.state}.` : "No offer." };
  return offer.state === "accepted"
    ? {
        offered: true,
        note: "Withdraws the offer. Its migration is already moving and goes on; only the owner's retries before any transfer leg executed stop.",
      }
    : {
        offered: true,
        note: "Withdraws the offer: the owner can no longer start the upgrade. It can be offered again later.",
      };
}

/** Friendly labels for the typed 409 refusals of the offer commands (`errorCode`). */
export const UPGRADE_OFFER_REFUSAL_LABELS: Readonly<Record<string, string>> = {
  notMigrationTarget: "Not the backend's upgrade target",
  walletNotProvisioned: "Wallet not provisioned",
  reactivatedNeedsSupport: "Reactivated wallet: needs support",
  planMissing: "No migration plan to the target",
  alreadyAtTarget: "Already on the target generation",
  notCurrentWallet: "Not the owner's current wallet",
  migrationActive: "A migration is already in flight",
  offerAlreadyLive: "An offer is already live",
  offerNotLive: "No live offer to withdraw",
  incorrectVersion: "Changed since you opened it",
};

/**
 * The offer commands' own refusals, else the migration-start refusals the offer gate shares with
 * `POST /walletMigrations` (`upgradeNotOffered`, and the cohort codes `vaultExitPending` /
 * `operationNeedsReview`).
 */
export function upgradeOfferRefusalLabel(code: string | null | undefined): string | undefined {
  return code
    ? (UPGRADE_OFFER_REFUSAL_LABELS[code] ?? migrationStartRefusalLabel(code))
    : undefined;
}

/** The timestamp that closed the offer's current round, for the list's "Ended" column. */
export function offerEndedAt(
  offer: Pick<WalletUpgradeOfferDoc, "state" | "consumedAt" | "withdrawnAt">,
): string | null {
  if (offer.state === "consumed") return offer.consumedAt ?? null;
  if (offer.state === "withdrawn") return offer.withdrawnAt ?? null;
  return null;
}

/** The fleet list's state filters: the live offers, or one state. */
export const OFFER_LIST_FILTERS: readonly { value: string; label: string; states: string[] }[] = [
  { value: "live", label: "Live", states: ["offered", "accepted"] },
  { value: "offered", label: "Offered", states: ["offered"] },
  { value: "accepted", label: "Accepted", states: ["accepted"] },
  { value: "consumed", label: "Consumed", states: ["consumed"] },
  { value: "withdrawn", label: "Withdrawn", states: ["withdrawn"] },
];
